/**
 * Is this a 409 whose body is the coded conflict `account_merged_retry`
 * (`{"detail": {"error": "account_merged_retry", "message": …}}`, vingapi#1858)?
 * Reads the error body, which the interceptor does not camelCase.
 */
export function isAccountMergedRetry(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const response = (error as AxiosError).response;
  if (response?.status !== 409) return false;
  const data = response.data as { detail?: unknown } | undefined;
  const detail = data && typeof data === 'object' ? data.detail : undefined;
  return !!detail && typeof detail === 'object' && (detail as { error?: unknown }).error === ACCOUNT_MERGED_RETRY;
}

/**
 * The ONE failure classifier for all non-consume error routing.
 *
 * Every axios error falls into exactly one class; the rest of the error-routing
 * layer (serverErrorRouting, telemetry, UI) branches on the class, never on raw
 * error shapes. Components NEVER inspect `ERR_NETWORK`, `ECONNABORTED`,
 * `response === undefined`, or `'Network Error'` — the guard test
 * (`failureClass.guard.test.ts`) enforces this.
 *
 * Consumers:
 *   - `serverErrorRouting.ts` — presentation routing (failure screen vs banner vs nothing)
 *   - `serverErrorRouting.shouldReportError` — telemetry gate
 *   - `consumeErrorPolicy.ts` — re-exports `isTransportFailure` for consume callers
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { ACCOUNT_MERGED_RETRY } from '@/api/retryPolicy';

/**
 * R2 (#239c): `transport` splits into `transport_timeout` (ECONNABORTED /
 * timeout) and `transport_network` (no response, not a timeout). The umbrella
 * `isTransportFailure` covers both; `isTransportTimeout` covers only the first.
 *
 * R3 (#239c): `cancel` now also covers unload aborts — errors raised while
 * `document.visibilityState === 'hidden'`, `signal.aborted`, or with messages
 * matching user/tab-close abort patterns.
 */
/**
 * #1076: the error the transport raises for a response that ended with status 0
 * (the browser killed the XHR without an error event). A browser abort, so it
 * classifies as `cancel`. Recognised by class, not by `code` text: no message
 * or visibilityState dependence, and it is not a server refusal code.
 */
export class BrowserAbortError extends axios.AxiosError {
  constructor(config?: InternalAxiosRequestConfig, request?: unknown) {
    super('Request aborted by the browser (status 0)', 'ERR_BROWSER_ABORT', config, request);
    this.name = 'BrowserAbortError';
  }
}

export type FailureClass = 'cancel' | 'transport_timeout' | 'transport_network' | 'server' | 'client' | 'other';

/** The circuit breaker rejects blocked requests with `new axios.Cancel('Circuit
 *  breaker is open')`. That is NOT a transport failure — it's a client-side block. */
export function isCircuitBreakerBlock(error: unknown): boolean {
  if (!axios.isCancel(error)) return false;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.includes('Circuit breaker');
}

/** R3 (#239c): an error raised during page unload / tab close / signal abort
 *  is a cancel, not a transport failure. The interceptor must not call
 *  noteApiResult for it. */
const UNLOAD_ABORT_MESSAGES = ['Load failed', 'The user aborted'];

function isPageHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function hasUnloadAbortMessage(error: AxiosError): boolean {
  const msg = error.message;
  return typeof msg === 'string' && UNLOAD_ABORT_MESSAGES.some((p) => msg.includes(p));
}

function isUnloadAbort(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const axErr = error as AxiosError;

  // Tab hidden or pagehide already fired
  if (isPageHidden()) return true;

  // Request signal was aborted (unmount / navigation)
  if (axErr.config?.signal?.aborted) return true;

  // Message patterns from Safari/Chrome on tab close
  return hasUnloadAbortMessage(axErr);
}

/**
 * Classify an error into one of the following classes:
 *
 *   - `cancel`            — a genuine axios cancellation (unmount / key change),
 *                           unload abort (R3), NOT the circuit-breaker cancel
 *   - `transport_timeout` — axios error with no response AND code ECONNABORTED
 *                           or axios timeout flag (R2)
 *   - `transport_network` — axios error with no response, not a timeout (R2)
 *   - `server`            — any HTTP response with status >= 500
 *   - `client`            — any HTTP response with status 4xx (400–499)
 *   - `other`             — non-axios throw, circuit-breaker cancel, or anything else
 */
export function classifyFailure(error: unknown): FailureClass {
  // #1336 (R4): a 409 `account_merged_retry` that reaches a caller was already
  // resent once (or came from a route the client does not resend): the server
  // failed the request, which is a server error to every surface.
  if (isAccountMergedRetry(error)) return 'server';

  if (axios.isCancel(error)) {
    if (isCircuitBreakerBlock(error)) return 'other';
    return 'cancel';
  }

  if (!axios.isAxiosError(error)) return 'other';

  // #1076: a status-0 response the transport rejected is a browser abort
  if (error instanceof BrowserAbortError) return 'cancel';

  // R3 (#239c): unload aborts are cancel, not transport
  if (isUnloadAbort(error)) return 'cancel';

  const axErr = error as AxiosError;
  const status = axErr.response?.status;

  // No response at all = transport failure (network error / timeout / DNS)
  if (status === undefined) {
    // R2 (#239c): split timeout from network failure
    if (axErr.code === 'ECONNABORTED' || axErr.code === 'ETIMEDOUT') return 'transport_timeout';
    return 'transport_network';
  }

  if (status >= 500) return 'server';
  if (status >= 400) return 'client';

  // 2xx/3xx responses that somehow reached the error path
  return 'other';
}

/**
 * Is this error a transport failure — a request that never completed a round
 * trip? Covers ECONNABORTED (timeout), ERR_NETWORK, "Request aborted", and any
 * other axios error with no response that is NOT a deliberate cancellation.
 *
 * Pure and dependency-light so both the api layer (interceptor) and the consume
 * UI (banner, persist queue) can share it.
 */
export function isTransportFailure(error: unknown): boolean {
  const cls = classifyFailure(error);
  return cls === 'transport_timeout' || cls === 'transport_network';
}

/**
 * R2 (#239c): is this specifically a timeout (ECONNABORTED / ETIMEDOUT)?
 * Used by the connectivity controller to decide 'slow' vs 'unreachable'.
 */
export function isTransportTimeout(error: unknown): boolean {
  return classifyFailure(error) === 'transport_timeout';
}

/**
 * Is this error handled centrally by `routeMutationError` / `routeQueryError`?
 *
 * Server (5xx) and transport (no-response) failures are both surfaced by the
 * central error routing: 5xx → the server message, transport → the
 * connectivity copy (#239), in the band of the dialog the mutation's hook was
 * called in while that dialog is open, else on the global banner (#1174,
 * owner Q-291 a). A per-site `onError` that pushes its own copy for these
 * classes would double the message. Use this predicate (not `isServerFailure`
 * alone) to skip the local copy for centrally-handled errors.
 *
 * Callers: reportMutationError (every mutation onError, #844), and the
 * dialogs that await a child-owned mutation through useAsyncAction with
 * `showError: false` (ShareDialog, BulkShareDialog). A caller-mode mutation
 * (`errorReporter: 'caller'`) is NOT centrally presented: its dialog presents
 * every failure with `presentCallerMutationError` (#1174-d).
 */
export function isGloballyRouted(error: unknown): boolean {
  const cls = classifyFailure(error);
  return cls === 'server' || cls === 'transport_timeout' || cls === 'transport_network';
}

/**
 * #1628-c1 (PM ruling 11-02): who ended a `cancel`-class request?
 *
 *   - `null`      — not a cancel (or it carries a response: a real refusal / 5xx
 *                   is never a cancel origin, even on a hidden tab).
 *   - `'ours'`    — we or the person stopped it: the tab is hidden or closing
 *                   (including a status-0 `BrowserAbortError` raised while
 *                   hidden), the request signal was aborted, or axios cancelled
 *                   it. Nothing is shown.
 *   - `'browser'` — the browser killed it while the page is visible and nothing
 *                   of ours aborted it (status 0, or a 'Load failed' / 'The
 *                   user aborted' message). The action failed; say so.
 *
 * `classifyFailure` is unchanged; this only splits its `cancel` class.
 */
export function cancelOrigin(error: unknown): 'ours' | 'browser' | null {
  if (classifyFailure(error) !== 'cancel') return null;
  const axErr = axios.isAxiosError(error) ? (error as AxiosError) : undefined;
  if (axErr?.response?.status !== undefined) return null;
  if (isPageHidden() || axErr?.config?.signal?.aborted || axios.isCancel(error)) return 'ours';
  if (error instanceof BrowserAbortError) return 'browser';
  if (axErr && hasUnloadAbortMessage(axErr)) return 'browser';
  return 'ours';
}
